import { useRouter, useSearchParams, usePathname } from "next/navigation";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ApiError } from "@/app/common-types/api-error.types";
import { RenewalPage } from "@/config/app-constant";
import { PRODUCT } from "@/constants";
import useFetchQuoteData from "@/hooks/api/quote/use-fetch-quote";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setMedicalData } from "@/store/slices/medical.slice";
import {
  clearQuoteErrorCode,
  setQuestionData,
  setQuote,
} from "@/store/slices/quote/quote.slice";
import { MedicalGateKeeperQuestions } from "@/types/MedicalTypes";
import { Organiser } from "@/types/Organiser";
import { checkOrganiserFields } from "@/utils/check-organiser-fields";
import getLoginPageUrl from "@/utils/get-login-page-url";
import { isFirstLoad } from "@/utils/get-renewal-session-status";
import handleError from "@/utils/handle-errors";

interface ErrorResponse {
  code?: number;
  errors?: {
    code: number;
    message: string;
  }[];
}

const withQuoteData = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  policy?: string,
  page?: string
) => {
  function WithSessionData(props: Readonly<P>) {
    const dispatch = useDispatch();
    const router = useRouter();
    const firstLoad = page === RenewalPage ? isFirstLoad() : false;
    const searchParams = useSearchParams();
    const quoteRef = searchParams.get("quoteId");
    const affiliate = searchParams.get("affiliate");
    const { data, error, loading } = useFetchQuoteData(
      quoteRef,
      affiliate,
      policy,
      firstLoad
    );
    const quoteState = useSelector((state: RootState) => state.quote);
    const { hideLoading } = useLoading();
    const pathname = usePathname();
    const { t } = useWizardTranslation();

    const handleOnClick = () => {
      router.push(getLoginPageUrl(PRODUCT));
    };

    useEffect(() => {
      if (data && !loading) {
        if (data.attributes?.organiser) {
          const organiserPath = t("organiser_path", "organiser");
          const contactDetailsPath = t(
            "contact_details_path",
            "contact-details"
          );

          const route = checkOrganiserFields(
            data?.attributes?.organiser as Organiser,
            pathname,
            organiserPath,
            contactDetailsPath,
            data?.attributes?.experimentDetails
          );
          if (route) {
            router.push(route);
          }
        }

        dispatch(setQuote({ ...data, quoteCompleted: { completed: true } }));
        dispatch(clearQuoteErrorCode());
        dispatch(
          setQuestionData({ ...data, quoteCompleted: { completed: true } })
        );

        const gateKeeperAnswers =
          data?.attributes?.travellerDetails?.gateKeeperAnswers;
        if (gateKeeperAnswers) {
          dispatch(
            setMedicalData(gateKeeperAnswers as MedicalGateKeeperQuestions)
          );
        }
      } else if (!loading && !data) {
        const gateKeeperAnswers =
          quoteState?.attributes?.travellerDetails?.gateKeeperAnswers;
        if (gateKeeperAnswers) {
          dispatch(
            setMedicalData(gateKeeperAnswers as MedicalGateKeeperQuestions)
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading, dispatch]);

    useEffect(() => {
      if (error as ApiError<ErrorResponse>) {
        hideLoading();
        handleError(
          error as ApiError<ErrorResponse>,
          router,
          dispatch,
          handleOnClick
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, dispatch, router, hideLoading]);

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent {...props} isLoading={loading} />;
  }

  return WithSessionData;
};

export default withQuoteData;
